import { useCallback, useEffect, useRef } from "react";

const TOOLBAR_BUTTONS = [
  { command: "bold", label: "B", title: "Negrito (Ctrl+B)", style: { fontWeight: 700 } },
  { command: "italic", label: "I", title: "Itálico (Ctrl+I)", style: { fontStyle: "italic" } },
  { command: "insertUnorderedList", label: "•", title: "Lista com marcadores" },
  { command: "insertOrderedList", label: "1.", title: "Lista numerada" },
  { command: "createLink", label: "🔗", title: "Inserir link", needsArg: true, prompt: "URL do link:" },
  { command: "unlink", label: "⛓️‍💥", title: "Remover link" },
  { command: "removeFormat", label: "Tx", title: "Limpar formatação" },
];

function execCommand(command, arg) {
  document.execCommand(command, false, arg);
}

function isContentEmpty(html) {
  if (!html) return true;
  const stripped = html
    .replace(/<br\s*\/?\s*>/gi, "")
    .replace(/<p>\s*<\/p>/gi, "")
    .replace(/&nbsp;/gi, "")
    .trim();
  return stripped === "" || stripped === "<p></p>";
}

export default function RichTextEditor({ value, onChange, placeholder = "Escreva aqui..." }) {
  const editorRef = useRef(null);
  const lastEmittedRef = useRef(value || "");

  useEffect(() => {
    const node = editorRef.current;
    if (!node) return;
    const next = value || "";
    // Sync DOM whenever the incoming value differs from what's currently
    // rendered. Comparing against innerHTML (instead of lastEmittedRef alone)
    // covers the initial mount, where the ref already matches `value` but the
    // editor div is still empty.
    if (next !== node.innerHTML) {
      node.innerHTML = next;
      lastEmittedRef.current = next;
    }
  }, [value]);

  const emitChange = useCallback(() => {
    const node = editorRef.current;
    if (!node) return;
    const html = isContentEmpty(node.innerHTML) ? "" : node.innerHTML;
    lastEmittedRef.current = html;
    onChange?.(html);
  }, [onChange]);

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const clipboard = e.clipboardData || window.clipboardData;
      const text = clipboard?.getData?.("text/plain") || "";
      if (!text) return;
      // insertText preserves newlines as <br> within the current block, so
      // pasting a multi-line snippet keeps its line breaks but loses fonts,
      // colors, span wrappers, etc.
      execCommand("insertText", text);
      emitChange();
    },
    [emitChange]
  );

  const handleToolbarClick = useCallback(
    (button) => (e) => {
      e.preventDefault();
      editorRef.current?.focus();
      let arg;
      if (button.needsArg) {
        // eslint-disable-next-line no-alert
        arg = window.prompt(button.prompt);
        if (!arg) return;
      }
      execCommand(button.command, arg);
      emitChange();
    },
    [emitChange]
  );

  // Keep the editor from inheriting Tailwind's reset (which strips list markers).
  const editorStyle = {
    minHeight: "120px",
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderTop: "none",
    borderBottomLeftRadius: "6px",
    borderBottomRightRadius: "6px",
    outline: "none",
    background: "white",
    fontSize: "14px",
    lineHeight: "1.5",
  };

  const isEmpty = isContentEmpty(value);

  return (
    <div className="rte-wrapper" style={{ width: "100%" }}>
      <div
        className="rte-toolbar"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          padding: "6px 8px",
          border: "1px solid #cbd5e1",
          borderTopLeftRadius: "6px",
          borderTopRightRadius: "6px",
          background: "#f8fafc",
        }}
      >
        {TOOLBAR_BUTTONS.map((btn) => (
          <button
            key={btn.command}
            type="button"
            title={btn.title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleToolbarClick(btn)}
            style={{
              minWidth: "32px",
              height: "30px",
              padding: "0 8px",
              border: "1px solid #e2e8f0",
              borderRadius: "4px",
              background: "white",
              cursor: "pointer",
              fontSize: "14px",
              ...(btn.style || {}),
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div style={{ position: "relative" }}>
        <div
          ref={editorRef}
          className="rte-content"
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onBlur={emitChange}
          onPaste={handlePaste}
          style={editorStyle}
        />
        {isEmpty && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "12px",
              color: "#94a3b8",
              pointerEvents: "none",
              fontSize: "14px",
            }}
          >
            {placeholder}
          </div>
        )}
      </div>
      <style>{`
        .rte-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.25rem 0; }
        .rte-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
        .rte-content li { margin: 0.125rem 0; }
        .rte-content p { margin: 0 0 0.5rem 0; }
        .rte-content a { color: #2563eb; text-decoration: underline; }
        .rte-content strong { font-weight: 700; }
        .rte-content em { font-style: italic; }
      `}</style>
    </div>
  );
}
